function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { nanoid } from "nanoid";
import useField from "../hooks/internal/useField";
import { mergeProps, areOptionsValid } from "../utils/component";
import Field from "./internal/Field";
import InternalRadioGroup from "./internal/InternalRadioGroup";
import { jsx as ___EmotionJSX } from "@emotion/core";
var COLORS = InternalRadioGroup.COLORS;

function isOptionSelected(options, value) {
  return options.findIndex(function (option) {
    return option.value === value;
  }) > -1;
}

var DEFAULT_PROPS = {
  color: InternalRadioGroup.DEFAULT_PROPS.color,
  disabled: false,
  optional: false,
  validate: function validate(value, _ref) {
    var isEmpty = _ref.isEmpty;

    if (isEmpty(value)) {
      return "Please make a selection.";
    }

    return null;
  }
};
RadioGroup.COLORS = COLORS;
RadioGroup.DEFAULT_PROPS = DEFAULT_PROPS;

function RadioGroup(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    optional: function optional(_optional) {
      return typeof _optional === "boolean";
    },
    options: function options(_options) {
      return areOptionsValid(_options);
    }
  });
  var name = mergedProps.name,
      label = mergedProps.label,
      options = mergedProps.options,
      columns = mergedProps.columns,
      helpText = mergedProps.helpText,
      disabled = mergedProps.disabled,
      optional = mergedProps.optional,
      validate = mergedProps.validate,
      validateData = mergedProps.validateData,
      testId = mergedProps.testId;

  if (!options) {
    throw new Error("RadioGroup options should have the following format: [{ label: \"option-label\", value: \"option-value\" }, ...]");
  }

  var _useState = useState(function () {
    return "radio-group-label-".concat(nanoid());
  }),
      _useState2 = _slicedToArray(_useState, 1),
      labelId = _useState2[0];

  var _useState3 = useState(function () {
    return "radio-group-aux-".concat(nanoid());
  }),
      _useState4 = _slicedToArray(_useState3, 1),
      auxId = _useState4[0];

  var cols = options.some(function (option) {
    return option.description;
  }) ? 1 : columns === undefined ? options.length : columns;
  var isEmpty = useCallback(function (value) {
    return isOptionSelected(options, value) === false;
  }, [options]);
  var data = useMemo(function () {
    return _objectSpread({
      isEmpty: isEmpty
    }, validateData && {
      data: validateData
    });
  }, [isEmpty, validateData]);

  var _useField = useField("RadioGroup", {
    name: name,
    disabled: disabled,
    optional: optional,
    validate: validate,
    data: data
  }),
      value = _useField.value,
      errors = _useField.errors,
      hasErrors = _useField.hasErrors,
      onFocus = _useField.onFocus,
      onBlur = _useField.onBlur,
      onChange = _useField.onChange,
      onMouseDown = _useField.onMouseDown;

  return ___EmotionJSX(Field, {
    optional: optional,
    disabled: disabled,
    label: label,
    labelId: labelId,
    auxId: auxId,
    helpText: helpText,
    errors: errors,
    testId: testId
  }, ___EmotionJSX(InternalRadioGroup, {
    name: name,
    labelId: labelId,
    auxId: auxId,
    options: options,
    columns: cols,
    color: props.color,
    disabled: disabled,
    isValid: !hasErrors,
    describedBy: helpText || hasErrors ? auxId : null,
    onFocus: onFocus,
    onBlur: onBlur,
    onMouseDown: onMouseDown,
    value: value,
    onChange: onChange
  }));
}

RadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    description: PropTypes.node,
    value: PropTypes.string.isRequired
  })).isRequired,
  columns: function columns(props) {
    if (props.columns !== undefined) {
      if (typeof props.columns !== "number") {
        return new Error("RadioGroup: columns must be a number (".concat(_typeof(props.columns), " found)"));
      }

      if (props.columns !== 1 && props.options.some(function (option) {
        return option.description;
      })) {
        return new Error("RadioGroup: option's description can only be used when columns={1}");
      }
    }
  },
  color: PropTypes.oneOf(COLORS),
  helpText: PropTypes.string,
  disabled: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onMouseDown: PropTypes.func,
  optional: PropTypes.bool,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  validateData: PropTypes.any,
  testId: PropTypes.string
};
export default RadioGroup;